import { MPOST, POST, truncate } from 'common/helpers.ts';
import React from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import useSWR from 'swr';
import useSWRMutation from 'swr/mutation';
import type { Leg } from 'types/Leg.ts';
import type { User } from 'types/User.ts';
import { Button } from 'ui/component/Button.tsx';
import { Modal } from 'ui/component/Modal.tsx';
import { Spinner } from 'ui/component/Spinner.tsx';
import { Select } from 'ui/control/Select.tsx';
import { useConfirm } from '#admin/component/DialogProvider.tsx';
import { getTranslatedError } from '#admin/helpers.ts';

type Props = {
  legs: Leg[];
  onSave?: (data: { driver_id: string; leg_ids: string[] }) => void;
  onClose: () => void;
  isOpen: boolean;
};

export const DriverAssignmentDialog: React.FC<Props> = ({ legs, onSave, onClose, isOpen }) => {
  const { t } = useTranslation();
  const confirm = useConfirm();

  const supplierId = legs[0].supplier_id;

  const {
    data: drivers,
    mutate: mutateDrivers,
    isLoading,
  } = useSWR<User[]>(['/user/list', { is_group: false, group_role$ilike: 'driver', descendant_of: supplierId, sort: [['name', 'asc']] }], POST);

  const validDrivers = React.useMemo(() => {
    const validDrivers = { none: t('options.none') };
    if (!drivers) return validDrivers;
    for (const driver of drivers) {
      const name = driver.name.toUpperCase();
      const id = truncate(driver.id, 8, '').toUpperCase();
      validDrivers[driver.id] = `${name} - ${id}`;
    }
    return validDrivers;
  }, [drivers, t]);

  const formMethods = useForm({
    mode: 'onTouched',
    criteriaMode: 'all',
    shouldUnregister: true,
    shouldUseNativeValidation: false,
    shouldFocusError: true,
    defaultValues: {
      driverId: legs[0].driver_id || 'none',
    },
  });

  const { handleSubmit, formState, watch } = formMethods;

  const selectedId = watch('driverId');
  const isBulk = legs.length > 1;
  const endpoint = isBulk ? '/leg/bulk-assign-driver' : selectedId === 'none' ? `/leg/${legs[0].id}/unassign-driver` : `/leg/${legs[0].id}/assign-driver`;
  const { trigger, isMutating, error } = useSWRMutation(endpoint, MPOST);

  const onSubmit = async ({ driverId }) => {
    if (driverId === 'none' && !(await confirm(t('dialogs.driver.unassignConfirm')))) return;
    if (driverId !== 'none') {
      const targetDriver = drivers?.find((driver) => driver.id === driverId) as User;
      if (!targetDriver || !drivers) return;
    }

    if (isBulk) {
      await trigger({ driver_id: driverId, leg_ids: legs.map((leg) => leg.id) });
    } else {
      await trigger(selectedId === 'none' ? {} : { driver_id: driverId });
    }

    void mutateDrivers();
    onSave?.({ driver_id: driverId, leg_ids: legs.map((leg) => leg.id) });
  };

  if (isLoading) return <Spinner centered={true} />;

  return (
    <Modal title={<h4>{t('dialogs.driver.title')}</h4>} isOpen={isOpen} onClose={onClose}>
      <FormProvider {...formMethods}>
        <form className="flex w-max-[900px] flex-col gap-2.5" onSubmit={handleSubmit(onSubmit)}>
          <div className="flex flex-col gap-2">
            <Select name={'driverId'} label={t('labels.driverId')} options={validDrivers} registerOptions={{ required: true }} />
          </div>

          <div className="mt-6 flex max-h-[400px] flex-col overflow-y-auto">
            <div className="grid h-[40px] grid-cols-4 items-center gap-2 border-grey-border border-b-1">
              <strong>Shipment ID</strong>
              <strong>From</strong>
              <strong></strong>
              <strong>To</strong>
            </div>

            {legs.map((leg) => (
              <div key={leg.id} className="grid h-[40px] grid-cols-4 items-center gap-2 border-grey-border border-b-1" data-shipment-id={leg.shipment_id} data-leg-id={leg.id}>
                <Link className="anchor anchor-novisit" to={`/shipment/${leg.shipment_logistics_ex_id}`} target="_blank">
                  {leg.shipment_logistics_ex_id}
                </Link>
                <span>{leg.origin_address_name}</span>
                <span className="text-center">{'→'}</span>
                <span>{leg.destination_address_name}</span>
              </div>
            ))}
          </div>

          <div className="mt-4 flex gap-5">
            <Button disabled={isMutating} className="blue-outlined" onClick={onClose}>
              {t('actions.back')}
            </Button>
            <Button
              type="submit"
              disabled={formState.isSubmitting || !formState.isValid || !formState.isDirty}
              loading={formState.isSubmitting}
              className="blue flex items-center justify-center"
            >
              {t('actions.save')}
            </Button>
          </div>
          {error && <span className="text-center text-red-500">{getTranslatedError(error, t) || t('dialogs.driver.error')}</span>}
        </form>
      </FormProvider>
    </Modal>
  );
};
